import React, { useCallback, useEffect } from 'react'
import { useSelector } from 'react-redux'
import { useTranslation } from 'react-i18next'
import classnames from 'classnames'
import PropTypes from 'prop-types'
import Skeleton from 'react-loading-skeleton'

import ProgressButton from './ProgressButton'

import useSuccessFormSubmit from './hooks'

import { keys } from '../../helpers/common'
import { handleScrollToFieldWithError, showToastForNonRenderedFields } from './helpers'

import { RENDERED_FIELDS } from '../../constants/forms'

import useStyles from './../../styles/common/drawerForms'

function Form({
  children,
  formName,
  formik,
  submitText = 'Save',
  onErrorSubmit,
  successSubmit,
  errorSelector,
  clearHandler,
  isLoadingSelector,
  onSuccessSubmit,
  showSubmit = true,
  className,
  formFooterClassName,
  buttonProps,
  isSubmitDataLoading,
  ...props
}) {
  const drawerFormClasses = useStyles()

  const { t } = useTranslation()

  const error = useSelector(errorSelector)
  const isLoading = useSelector(isLoadingSelector)

  const { setStatus, resetForm, handleSubmit, validateForm, status, values } = formik

  const serverErrors = status?.serverErrors
  const renderedFields = values[RENDERED_FIELDS]

  const handleSuccessSubmit = useSuccessFormSubmit({ onSuccessSubmit, formName, resetForm })

  const handleFormSubmit = useCallback(
    e => {
      e.preventDefault()
      handleSubmit()

      validateForm().then(err => {
        const errArray = keys(err)
        if (errArray.length) {
          handleScrollToFieldWithError(formName)
        }
      })
    },
    [handleSubmit, validateForm, formName]
  )

  useEffect(
    () => {
      const errorsObj = error && error.errors
      if (errorsObj) {
        // handle case when server returns errors for non-rendered fields, and error is not shown this could happen in 2 cases:
        // 1. fields are conditionally rendered, for example using Spoiler, Dropdown components or etc..
        // 2. FE structure is different to BE, as we usually format formik structure to BE payload
        showToastForNonRenderedFields(errorsObj, renderedFields)
        // BE errors sets to Formik status
        setStatus({ serverErrors: error.errors })
        // error callback
        onErrorSubmit && onErrorSubmit(error)
      }
    },
    // eslint-disable-next-line
    [setStatus, error, renderedFields]
  )

  // Scroll to field with server error
  useEffect(() => {
    if (serverErrors) {
      handleScrollToFieldWithError(formName)
    }
  }, [serverErrors, formName])

  return (
    <form {...props} className={className} onSubmit={handleFormSubmit} data-form-name={formName}>
      {children}
      {showSubmit && (
        <footer className={classnames(drawerFormClasses.formFooter, formFooterClassName)}>
          {isSubmitDataLoading ? (
            <Skeleton height={37} width={65} />
          ) : (
            <ProgressButton
              formError={error}
              isFormLoading={isLoading}
              successSubmit={successSubmit}
              clearHandler={clearHandler}
              onSuccessSubmit={handleSuccessSubmit}
              {...buttonProps}
            >
              {t(submitText)}
            </ProgressButton>
          )}
        </footer>
      )}
    </form>
  )
}

Form.propTypes = {
  formik: PropTypes.object.isRequired,
  formName: PropTypes.string.isRequired,
  children: PropTypes.node,
  showSubmit: PropTypes.bool,
  submitText: PropTypes.string,
  onErrorSubmit: PropTypes.func,
  successSubmit: PropTypes.bool,
  onSuccessSubmit: PropTypes.func,
  errorSelector: PropTypes.func.isRequired,
  isLoadingSelector: PropTypes.func.isRequired,
  clearHandler: PropTypes.func,
  className: PropTypes.string,
  formFooterClassName: PropTypes.string,
  buttonProps: PropTypes.object,
  isSubmitDataLoading: PropTypes.bool
}

export default Form
